import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { VocabItem } from "../types";
import FlashCard from "../components/vocabulary/FlashCard";
import GenerationLoader from "../components/GenerationLoader";

export default function VocabularyPage() {
  const [vocab, setVocab] = useState<VocabItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getVocabulary()
      .then(setVocab)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex-1 flex items-center justify-center">
      <GenerationLoader
        messages={["Loading your vocabulary...", "Fetching words Elena collected..."]}
        subtitle=""
      />
    </div>
  );

  if (vocab.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-6">
        <p className="text-text-tertiary italic text-sm text-center leading-relaxed max-w-sm">
          No vocabulary yet. Start a session and Elena will collect new words for you as you go.
        </p>
      </div>
    );
  }

  // Group by session
  const bySession = vocab.reduce<Record<number, VocabItem[]>>((acc, v) => {
    const key = v.session_id ?? 0;
    (acc[key] ??= []).push(v);
    return acc;
  }, {});

  const sessionIds = Object.keys(bySession)
    .map(Number)
    .sort((a, b) => b - a);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto w-full px-5 sm:px-8 py-10">
        <header className="mb-8 animate-rise">
          <h1>Vocabulary</h1>
          <p className="text-text-tertiary text-[0.9375rem] mt-1">
            Words Elena collected for you — tap a card to flip it.
          </p>
        </header>

        {sessionIds.map((sid) => (
          <section key={sid} className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="section-label">Session {sid}</span>
              <span className="flex-1 h-px bg-border-soft" />
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-4">
              {bySession[sid].map((v) => (
                <FlashCard key={v.id} vocab={v} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
